import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6C',
  tealDark: '#0D3135',
  copper: '#AB9381',
  cream: '#F7F8F6',
  nearBlack: '#0A0B0B',
}

const BOOKING_URL =
  'https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2'

const NEIGHBORHOODS: Record<string, { name: string; tagline: string; description: string }> = {
  ankeny: {
    name: 'Ankeny',
    tagline: 'Where Family-First Meets Home-First.',
    description:
      'Ankeny families are among the busiest in the metro. Between school, sports, and careers, home maintenance is the last thing anyone wants to think about. Hudson My Home™ gives Ankeny homeowners their time back with proactive, professional care.',
  },
  'west-des-moines': {
    name: 'West Des Moines',
    tagline: 'Luxury Living, Professionally Managed.',
    description:
      'West Des Moines homeowners expect a certain standard. Hudson My Home™ delivers it — with your own Hudson who knows your home inside and out, proactive seasonal care, and the peace of mind that comes from knowing everything is handled.',
  },
  'prairie-trail': {
    name: 'Prairie Trail',
    tagline: 'Modern Homes Deserve Modern Care.',
    description:
      'Prairie Trail is one of the most thoughtfully designed communities in Iowa. Your home deserves the same level of intention. Hudson My Home™ provides proactive management tailored to newer builds and the unique needs of planned communities.',
  },
}

const METRO_FALLBACK = {
  name: 'Des Moines Metro',
  tagline: 'Professional home management where you live.',
  description:
    'From Ankeny to West Des Moines and everywhere in between, Hudson My Home™ keeps your home running smoothly with proactive care, a dedicated team, and same-day responsiveness—so you stay informed, not involved.',
}

const FEATURES = [
  {
    title: 'Dedicated Professional',
    body: 'Your own Hudson assigned to your home',
  },
  {
    title: 'Proactive Care',
    body: 'Seasonal maintenance handled before you think about it',
  },
  {
    title: 'Same-Day Response',
    body: 'When something comes up, we respond the same day',
  },
] as const

const NeighborhoodPage: React.FC = () => {
  const { neighborhoodSlug } = useParams<{ neighborhoodSlug: string }>()
  const slug = neighborhoodSlug ?? ''
  const n = NEIGHBORHOODS[slug] ?? METRO_FALLBACK

  const featuresRef = useRef<HTMLElement>(null)
  const featuresInView = useInView(featuresRef, { once: true, margin: '-80px' })

  const ctaRef = useRef<HTMLElement>(null)
  const ctaInView = useInView(ctaRef, { once: true, margin: '-80px' })

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section
          className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden bg-white"
          aria-labelledby="neighborhood-hero-heading"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background: `linear-gradient(165deg, #ffffff 0%, #ffffff 35%, rgba(20, 95, 107, 0.06) 100%)`,
            }}
          />
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 relative">
            <motion.h1
              id="neighborhood-hero-heading"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease }}
              className="font-serif font-normal text-grey-950 max-w-4xl"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Home Management for {n.name} Homeowners
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12, ease }}
              className="mt-5 font-serif italic text-lg sm:text-xl max-w-3xl"
              style={{ color: colors.teal }}
            >
              {n.tagline}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.16, ease }}
              className="mt-8 font-sans text-base sm:text-lg leading-[1.65] text-grey-700 max-w-2xl"
            >
              {n.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24, ease }}
              className="mt-10"
            >
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 py-3.5 px-8 font-sans font-semibold text-white text-base transition-opacity hover:opacity-90 rounded-none"
                style={{ backgroundColor: colors.copper }}
              >
                Book Your Consultation
                <ArrowRight className="w-4 h-4" aria-hidden />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section
          ref={featuresRef}
          className="py-20 lg:py-28"
          style={{ backgroundColor: colors.cream }}
          aria-labelledby="neighborhood-features-heading"
        >
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.h2
              id="neighborhood-features-heading"
              initial={{ opacity: 0, y: 18 }}
              animate={featuresInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease }}
              className="font-serif font-normal text-grey-950 mb-12 lg:mb-16"
              style={{
                fontSize: 'clamp(1.65rem, 3vw, 2.25rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              Why homeowners choose Hudson
            </motion.h2>
            <div className="grid gap-10 md:grid-cols-3 md:gap-8 lg:gap-12">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, ease, delay: 0.12 * i }}
                  className="border-l-2 pl-6"
                  style={{ borderColor: colors.teal }}
                >
                  <div
                    className="flex h-9 w-9 items-center justify-center mb-4 rounded-none"
                    style={{ backgroundColor: colors.tealDark }}
                  >
                    <Check className="w-5 h-5 text-white" strokeWidth={2.5} aria-hidden />
                  </div>
                  <h3
                    className="font-serif text-xl text-grey-950 mb-2"
                    style={{ letterSpacing: '-0.02em' }}
                  >
                    {f.title}
                  </h3>
                  <p className="font-sans text-base leading-relaxed text-grey-600">{f.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          ref={ctaRef}
          className="py-20 lg:py-28"
          style={{ backgroundColor: colors.tealDark }}
          aria-labelledby="neighborhood-cta-heading"
        >
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.h2
              id="neighborhood-cta-heading"
              initial={{ opacity: 0, y: 60 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease }}
              className="font-serif font-normal text-white max-w-2xl mx-auto"
              style={{
                fontSize: 'clamp(1.65rem, 3.2vw, 2.35rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              Ready to Experience Home Management Done Right?
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.1 }}
              className="mt-10 flex flex-col items-center gap-4"
            >
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 py-3.5 px-8 font-sans font-semibold text-base transition-opacity hover:opacity-90 rounded-none"
                style={{ backgroundColor: colors.copper, color: '#fff' }}
              >
                Schedule Your Free Consultation
                <ArrowRight className="w-4 h-4" aria-hidden />
              </a>
              <p className="font-sans text-sm text-white/75">
                No commitment required. Cancel anytime.
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default NeighborhoodPage
